import { updateCart as updateCartApi } from "@iewcasia/Services/CartService";
import {
    CHECKOUT_SHIPPING_HANDLED_ERROR,
    isNoCarriersProcessedError,
} from "@iewcasia/Store/Handlers/CheckoutShipping/CarrierAddressError";
import { resolveGuestCheckoutAddress } from "@iewcasia/Store/Handlers/CheckoutShipping/ResolveGuestCheckoutAddress";
import { addToChainBefore, replaceInChain, StrictInputDispatch } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getCart } from "@insite/client-framework/Services/CartService";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    AddOrUpdateShipTo,
    chain,
    UpdateCart,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { BillToModel, CartModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

addToChainBefore(chain, AddOrUpdateShipTo, props => {
    const state = props.getState();

    if (!getCurrentUserIsGuest(state)) {
        return;
    }

    const { billingAddressFormState, shippingAddressFormState, cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const storeShipTo = getShipToState(state, cart?.shipToId).value;
    const resolvedAddress = resolveGuestCheckoutAddress(
        billingAddressFormState?.address,
        shippingAddressFormState?.address,
        storeShipTo,
    );

    if (!resolvedAddress) {
        return;
    }

    // IEIXOP-6877: One-time/create-new ship-tos use the label as a temporary client id
    // (cleanShipToModel). Do not copy them into billing or UpdateBillTo PATCHes
    // /billtos/{label} and returns 400.
    const resolvedShipTo = resolvedAddress as ShipToModel;
    if (resolvedShipTo.oneTimeAddress || resolvedShipTo.isNew) {
        props.dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address: resolvedShipTo,
        });
        return;
    }

    props.dispatch({
        type: "Pages/CheckoutShipping/SetShippingAddress",
        address: resolvedAddress as ShipToModel,
    });
    props.dispatch({
        type: "Pages/CheckoutShipping/SetBillingAddress",
        address: resolvedAddress as BillToModel,
    });
});

/** IEIXOP-6930: Persist empty-carrier cart in Redux and open the Shipping modal; skip onSuccess. */
const openCarrierAddressErrorAndStopChain = (
    props: { dispatch: StrictInputDispatch },
    cart: CartModel,
    isCurrent: boolean,
) => {
    const cartWithoutCarriers: CartModel = {
        ...cart,
        carriers: [],
        carrier: null,
        shipVia: null,
        properties: {
            ...(cart.properties ?? {}),
            shippingOptions: "",
        },
    };

    props.dispatch({
        type: "Data/Carts/CompleteLoadCart",
        model: cartWithoutCarriers,
        isCurrent,
        replaceCart: true,
    });
    props.dispatch({
        type: "Pages/CheckoutShipping/CompleteUpdateCart",
    });
    props.dispatch({
        type: "Custom/CheckoutShipping/SetCarrierAddressErrorModal",
        isCarrierAddressErrorModalOpen: true,
    });

    const error: { [CHECKOUT_SHIPPING_HANDLED_ERROR]?: boolean; status?: number; errorMessage?: string } = {
        status: 400,
        errorMessage: "No carriers",
    };
    error[CHECKOUT_SHIPPING_HANDLED_ERROR] = true;
    throw error;
};

replaceInChain(chain, UpdateCart, async props => {
    const state = props.getState();
    const isGuest = getCurrentUserIsGuest(state);
    const checkoutCartId = state.pages.checkoutShipping.cartId;

    const updatedCart = { ...props.cart };

    if (isGuest) {
        //  We dont need for guest on this page, since its causing issues with the shippingOptions custom prop value
        updatedCart.properties = {};
    }

    props.apiParameter.cart = { ...updatedCart };

    props.apiParameter.additionalQueryStringParameters = {
        ...props.apiParameter.additionalQueryStringParameters,
        ignoreProperties: true,
    };

    try {
        props.apiResult = await updateCartApi(props.apiParameter);

        // IEIXOP-6930: After soft-success (backend allows empty carriers), reload cart with carriers
        // so Continue Anyway / Review & Pay do not keep the previous address' carrier list.
        const cartResult = await getCart({
            cartId: checkoutCartId || API_URL_CURRENT_FRAGMENT,
            expand: ["carriers", "shipping", "cartLines"],
            allowInvalidAddress: true,
            forceRecalculation: true,
        });

        props.dispatch({
            type: "Data/Carts/CompleteLoadCart",
            model: cartResult.cart,
            isCurrent: !checkoutCartId,
            replaceCart: true,
        });

        if (!(cartResult.cart.carriers?.length)) {
            openCarrierAddressErrorAndStopChain(props, cartResult.cart, !checkoutCartId);
        }
    } catch (error) {
        // Already handled above (empty carriers after successful PATCH).
        if (
            error &&
            typeof error === "object" &&
            (error as { [CHECKOUT_SHIPPING_HANDLED_ERROR]?: boolean })[CHECKOUT_SHIPPING_HANDLED_ERROR]
        ) {
            throw error;
        }

        // Clear isUpdatingCart so Checkout Shipping LoadingOverlay does not stick when the chain
        // breaks before CompleteUpdateCart.
        props.dispatch({
            type: "Pages/CheckoutShipping/CompleteUpdateCart",
        });

        // IEIXOP-6930: Legacy hard-fail path ("No carriers...") — clear stale carriers in Redux.
        if (isNoCarriersProcessedError(error) && error && typeof error === "object") {
            const existingCart = checkoutCartId
                ? getCartState(props.getState(), checkoutCartId).value
                : getCurrentCartState(props.getState()).value;
            if (existingCart) {
                openCarrierAddressErrorAndStopChain(props, existingCart, !checkoutCartId);
            }
            (error as { [CHECKOUT_SHIPPING_HANDLED_ERROR]?: boolean })[CHECKOUT_SHIPPING_HANDLED_ERROR] = true;
            props.dispatch({
                type: "Custom/CheckoutShipping/SetCarrierAddressErrorModal",
                isCarrierAddressErrorModalOpen: true,
            });
        }

        throw error;
    }
});
